import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import client from '../api/client';
import { getFriendlyApiError } from '../utils/apiErrors';
import { trackCarFunnelStep, trackGenerateLead, trackFunnelStep } from '../utils/analytics';
import { findHoustonCarLanding } from '../data/houstonCarLandings';
import {
  leadTypeLabel,
  parseQuoteLeadType,
  saveLeadSession,
  markLeadConversionFired,
  type LeadContact,
  type QuoteLeadType,
} from '../utils/leadSession';
import './CarInsuranceInformationPage.css';

function trackShortFormView(type: QuoteLeadType): void {
  if (type === 'car') {
    trackCarFunnelStep('form_view', { entry_point: 'short_lead' });
    return;
  }
  if (type === 'pet') {
    trackFunnelStep('form_view', { entry_point: 'short_lead' });
  }
}

function resolveAdVehicle(searchParams: URLSearchParams): {
  vehicleMake?: string;
  vehicleModel?: string;
} {
  const makeSlug = (searchParams.get('make') || sessionStorage.getItem('houstonAdMake') || '')
    .trim()
    .toLowerCase();
  const modelSlug = (searchParams.get('model') || sessionStorage.getItem('houstonAdModel') || '')
    .trim()
    .toLowerCase();
  if (!makeSlug && !modelSlug) return {};

  const known = findHoustonCarLanding(makeSlug || undefined, modelSlug || undefined);
  if (known) {
    return { vehicleMake: known.make, vehicleModel: known.model };
  }

  const titleCase = (slug: string) =>
    slug
      .split('-')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');

  return {
    ...(makeSlug ? { vehicleMake: titleCase(makeSlug) } : {}),
    ...(modelSlug ? { vehicleModel: titleCase(modelSlug) } : {}),
  };
}

function GetQuotePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const typeParam = searchParams.get('type');
  const insuranceType = useMemo(() => parseQuoteLeadType(typeParam), [typeParam]);
  const adVehicle = useMemo(() => resolveAdVehicle(searchParams), [searchParams]);

  const [formData, setFormData] = useState<LeadContact>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!insuranceType) return;
    const previousTitle = document.title;
    const label = leadTypeLabel(insuranceType);
    document.title = `Get a ${label.toLowerCase()} insurance quote | GetSkimp`;
    sessionStorage.setItem('insuranceType', insuranceType);
    trackShortFormView(insuranceType);
    return () => {
      document.title = previousTitle;
    };
  }, [insuranceType]);

  if (!insuranceType) {
    return <Navigate to="/" replace />;
  }

  const label = leadTypeLabel(insuranceType);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { data } = await client.post<{
        lead: { id: number };
      }>('/insurance/lead', {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        insuranceType,
        location: 'TX',
        zipCode: sessionStorage.getItem('zipCode') || undefined,
        ...(adVehicle.vehicleMake ? { vehicleMake: adVehicle.vehicleMake } : {}),
        ...(adVehicle.vehicleModel ? { vehicleModel: adVehicle.vehicleModel } : {}),
      });

      const contact: LeadContact = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
      };

      saveLeadSession({
        leadId: data.lead.id,
        contact,
        insuranceType,
      });

      if (insuranceType === 'car') {
        trackCarFunnelStep('car_lead_submit');
      }
      await trackGenerateLead(insuranceType);
      markLeadConversionFired();

      navigate('/get-quote/thank-you');
    } catch (err: unknown) {
      setError(getFriendlyApiError(err, 'Something went wrong. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container car-lead-page">
      <div className="card car-lead-card">
        <div className="card-header">Get your {label.toLowerCase()} insurance quote</div>
        <p className="car-lead-intro">
          Share your contact info and we&apos;ll follow up with options. Takes under a minute.
        </p>

        {error && <div className="alert alert-error">{error}</div>}

        <form onSubmit={handleSubmit}>
          <input type="hidden" name="location" value="TX" readOnly />

          <div className="car-lead-row">
            <div className="form-group">
              <label htmlFor="firstName">First Name</label>
              <input
                id="firstName"
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
                autoComplete="given-name"
                placeholder="John"
              />
            </div>
            <div className="form-group">
              <label htmlFor="lastName">Last Name</label>
              <input
                id="lastName"
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
                autoComplete="family-name"
                placeholder="Doe"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              autoComplete="email"
              placeholder="your@email.com"
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone">Phone Number</label>
            <input
              id="phone"
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
              autoComplete="tel"
              placeholder="(555) 123-4567"
            />
          </div>

          <div className="form-group">
            <label htmlFor="insuranceType">Insurance Type</label>
            <select
              id="insuranceType"
              name="insuranceType"
              value={insuranceType}
              disabled
              aria-readonly="true"
            >
              <option value={insuranceType}>{label}</option>
            </select>
          </div>

          <div className="car-lead-actions">
            <Link to="/" className="btn btn-secondary">
              Back
            </Link>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default GetQuotePage;
